import { CModal } from "@coreui/react";
import { useMemo, useReducer, useState } from "react";
import DateFilters from "../../components/DateFilters";
import { SidebarIcon } from "../../components/icons/SidebarIcons";
import Pagination from "../../components/Pagination";
import RenderTable from "../../components/RenderTable";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import { RideStatus, RideStatusLabels } from "../../helpers/enums";
import { pluralize } from "../../helpers/general";
import { useGetRidesByIds } from "../../hooks/useGetFullRides";
import { useRides } from "../../hooks/useRides";
import { general } from "../../locales/general";
import RidersTable from "../taxi/RidersTable";
import PayRideModalContent from "./PayRideModalContent";

type DriverRidesProps = {
  driver: Driver | undefined;
};
const pageSize = 10;

type ModalState = {
  visible: boolean;
  target: "discount" | "bonus" | "balance" | null;
};

export default function DriverRides({ driver }: DriverRidesProps) {
  const [selectedRides, dispatch] = useReducer(
    bulkDeleteReducer,
    bulkDeleteState,
  );
  const defaultOption = useMemo(
    () => ({
      driverId: driver?.id,
    }),
    [driver?.id],
  );

  const [modalState, setModalState] = useState<ModalState>({
    visible: false,
    target: null,
  });

  const {
    queries,
    addQuery,
    isLoading,
    rides,
    setDates,
    clearQuickFilter,
    unpaidCounts,
    isQuickFilterActive,
    activeQuickFilter,
    handleGetUnpaidBonus,
    handleGetUnpaidCoupon,
    handleGetUnpaidBalance,
  } = useRides(defaultOption);




  const { data: allSelectedRides = [] } = useGetRidesByIds(selectedRides.ids);

  // console.log(allSelectedRides);

  const balanceAmountTotal = useMemo(() => {
    return allSelectedRides.reduce((acc: number, ride: Ride) => {
      return acc + (Number(ride.fare) || Number(ride.originalFare) || 0);
    }, 0);
  }, [allSelectedRides]);

  // Check if ALL selected rides are eligible for user balance payment
  const allSelectedRidesEligibleForBalance = useMemo(() => {
    if (!allSelectedRides.length) return false;

    return allSelectedRides.every((ride: Ride) => {
      const isUserBalancePayment = ride.paymentMethod === "user_balance";

      const isBalanceNotPaid = !ride.metaData?.userBalancePayment?.paid;

      const isCompletedAndValid =
        ride.status === RideStatus.COMPLETED &&
        ride.metaData?.validRide === true;

      return isUserBalancePayment && isBalanceNotPaid && isCompletedAndValid;
    });
  }, [allSelectedRides]);

  const discountAmountTotal = useMemo(() => {
    return allSelectedRides.reduce((acc: any, ride: Ride) => {
      const difference = Number(ride.originalFare) - Number(ride.fare);
      return acc + (difference > 0 ? difference : 0);
    }, 0);
  }, [allSelectedRides]);

  const allRidesHaveUnpaidCoupon = useMemo(() => {
    if (!allSelectedRides.length) return false;

    return allSelectedRides.every((ride: Ride) => {
      const coupon = ride.priceCalculation?.coupon;
      const metaDataCoupon = ride.metaData?.couponDiscount;

      const isApplied = coupon?.isCouponApplied === true;

      const isPaid = metaDataCoupon?.paid === true;

      return isApplied && !isPaid;
    });
  }, [allSelectedRides]);

  const allRidesHaveUnpaidBonus = useMemo(() => {
    if (!allSelectedRides.length) return false;

    return allSelectedRides.every((ride: Ride) => {
      const bonus = ride.metaData?.driverPayment;

      const isPaid = bonus?.paid === true;

      return !isPaid;
    });
  }, [allSelectedRides]);

  if (!driver) return null;

  const resetSelectedRides = () => {
    dispatch({ type: "", id: "" });
  };

  return (
    <div className="p-4">
      <div className="d-flex justify-content-end"></div>
      <div className="d-flex flex-wrap align-items-center gap-4 px-3 mt-4 mb-3">
        <DateFilters
          label="Date"
          onChange={setDates}
          onPeriodChange={(period) => {
            addQuery("period", period || undefined);
            resetSelectedRides();
          }}
        />
        <div>
          <label htmlFor="status">Statut</label>
          <select
            name="status"
            id="status"
            value={queries.status || ""}
            onChange={(e) => addQuery("status", e.target.value as RideStatus)}
          >
            <option value={""}>Tous</option>
            {Object.entries(RideStatusLabels).map(([key, value]) => (
              <option value={key}>{value}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="status">Mode de paiement</label>
          <select
            name="status"
            id="status"
            value={queries.paymentMethod || ""}
            onChange={(e) => addQuery("paymentMethod", e.target.value as any)}
          >
            <option value="">Tous</option>
            <option value="cash">Espèces</option>
            <option value="user_balance">Solde utilisateur</option>
          </select>
        </div>
        <div>
          <label htmlFor="isPayed" className="ms-1">
            Bonus Payé ?
          </label>
          <select
            name="isPayed"
            id="isPayed"
            value={queries.payed || ""}
            onChange={(e) => addQuery("payed", e.target.value as any)}
          >
            <option value={""}>Tous</option>
            <option value="true">Payé</option>
            <option value="false">Non payé</option>
          </select>
        </div>
        <div>
          <label htmlFor="couponPaid" className="ms-1">
            Coupon Payé ?
          </label>
          <select
            name="couponPaid"
            id="couponPaid"
            value={queries.couponPaid || ""}
            onChange={(e) => addQuery("couponPaid", e.target.value as any)}
          >
            <option value={""}>Tous</option>
            <option value="true">Payé</option>
            <option value="false">Non payé</option>
          </select>
        </div>
        <div>
          <label htmlFor="isValid" className="ms-1">
            Le trajet est-il valid ?
          </label>
          <select
            name="isValid"
            id="isValid"
            value={queries.isValidRide || ""}
            onChange={(e) => addQuery("isValidRide", e.target.value as any)}
          >
            <option value={""}>Tous</option>
            <option value="true">Valide</option>
            <option value="false">Non valide</option>
            <option value="pending">En attente</option>
          </select>
        </div>
        <div>
          <label htmlFor="userBalancePaid" className="ms-1">
            Solde utilisateur payé ?
          </label>
          <select
            name="userBalancePaid"
            id="userBalancePaid"
            onChange={(e) => addQuery("userBalancePaid", e.target.value as any)}
            value={queries.userBalancePaid || ""}
          >
            <option value="">Tous</option>
            <option value="true">Payé</option>
            <option value="false">Non payé</option>
          </select>
        </div>
      </div>

      <div>
        <div className="d-flex flex-wrap gap-3 mb-4 pb-3 border-top  border-bottom pt-4 mt-5 pb-4  ">
          <button
            className={`btn ${activeQuickFilter === "bonus" ? "btn-danger" : "btn-outline-danger"} d-flex align-items-center gap-2 p-3`}
            onClick={() => {
              handleGetUnpaidBonus();
              resetSelectedRides();
            }}
            disabled={!unpaidCounts?.bonusUnpaid}
          >
            <span className="badge bg-white text-danger rounded-pill">
              {unpaidCounts?.bonusUnpaid ?? 0}
            </span>
            <span>Bonus Impayés</span>
            {activeQuickFilter === "bonus" && (
              <button
                className="btn-close btn-close-white ms-2"
                onClick={(e) => {
                  e.stopPropagation();
                  clearQuickFilter();
                  resetSelectedRides();
                }}
              />
            )}
          </button>

          <button
            className={`btn ${activeQuickFilter === "coupon" ? "btn-warning" : "btn-outline-warning"} d-flex align-items-center gap-2`}
            onClick={() => {
              handleGetUnpaidCoupon();
              resetSelectedRides();
            }}
            disabled={!unpaidCounts?.couponUnpaid}
          >
            <span className="badge bg-white text-warning rounded-pill">
              {unpaidCounts?.couponUnpaid ?? 0}
            </span>
            <span>Coupons Impayés</span>
            {activeQuickFilter === "coupon" && (
              <button
                className="btn-close btn-close-white ms-2"
                onClick={(e) => {
                  e.stopPropagation();
                  clearQuickFilter();
                  resetSelectedRides();
                }}
              />
            )}
          </button>

          <button
            className={`btn ${activeQuickFilter === "balance" ? "btn-info" : "btn-outline-info"} d-flex align-items-center gap-2`}
            onClick={() => {
              handleGetUnpaidBalance();
              resetSelectedRides();
            }}
            disabled={!unpaidCounts?.balanceUnpaid}
          >
            <span className="badge bg-white text-info rounded-pill text-black">
              {unpaidCounts?.balanceUnpaid ?? 0}
            </span>
            <span>Solde Util. Impayé</span>
            {activeQuickFilter === "balance" && (
              <button
                className="btn-close btn-close-white ms-2"
                onClick={(e) => {
                  e.stopPropagation();
                  clearQuickFilter();
                  resetSelectedRides();
                }}
              />
            )}
          </button>

          {isQuickFilterActive && (
            <button
              className="btn btn-secondary btn-sm ms-auto"
              onClick={clearQuickFilter}
            >
              Voir tous les trajets
            </button>
          )}
        </div>
        {queries.fetchAll && (
          <div className="alert alert-warning d-flex align-items-center gap-2 mb-3">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span>
              Affichage de <strong>tous</strong> les trajets correspondants (
              {rides?.items?.length ?? 0}). Pagination désactivée.
            </span>
          </div>
        )}
        <div className="d-flex align-items-center justify-content-between gap-4 mt-5">
          <div className="d-flex gap-4">
            <button
              className="btn btn-danger text-white rounded-sm"
              type="button"
              disabled={
                selectedRides.ids.length === 0 || !allRidesHaveUnpaidCoupon
              }
              onClick={() =>
                setModalState({
                  ...modalState,
                  visible: true,
                  target: "discount",
                })
              }
            >
              Payer les frais de coupon
            </button>
            <button
              className="btn btn-danger text-white rounded-sm"
              type="button"
              disabled={
                selectedRides.ids.length === 0 ||
                selectedRides.ids.length % 10 !== 0 ||
                !allRidesHaveUnpaidBonus
              }
              onClick={() =>
                setModalState({ ...modalState, visible: true, target: "bonus" })
              }
            >
              Payer le bonus de trajet
            </button>
            <button
              className="btn btn-danger text-white rounded-sm"
              type="button"
              disabled={
                selectedRides.ids.length === 0 ||
                !allSelectedRidesEligibleForBalance
              }
              onClick={() =>
                setModalState({
                  ...modalState,
                  visible: true,
                  target: "balance",
                })
              }
            >
              Payer le solde de trajet
            </button>
          </div>
          <div
            className="d-flex  align-items-center rounded-3 justify-content-center w-fit w-auto gap-2 bg-gradient-primary p-3"
            style={{ alignSelf: "flex-center", flexWrap: "wrap" }}
          >
            <div className="d-flex gap-2">
              <SidebarIcon.RidesSVG className="fill-white" />
              <p className="fs-5 fw-bold m-0  text-white text-pre">
                {rides?.meta?.totalItems}{" "}
                {pluralize("Trajet", rides?.meta?.totalItems)}{" "}
              </p>
            </div>
            {/* <p className="fs-5 fw-bold m-0  text-white text-pre">
              {unpaidRides?.currentCount} Bonus Impayés
            </p> */}
          </div>
        </div>
      </div>

      <div className="table-responsive overflow-auto">
        <RenderTable
          loading={isLoading}
          render={() =>
            rides?.items && rides?.items?.length > 0 ? (
              <RidersTable
                riders={rides?.items}
                onRiderSelect={dispatch}
                selectedRides={selectedRides.ids}
                showDriver={false}
                ShowSelectCount={true}
              />
            ) : (
              <p className="text-center fs-5 my-2">{general.fr.noData}</p>
            )
          }
        />
        {!queries.fetchAll && (
          <Pagination
            currentPage={queries.page!}
            onPageChange={(page) => {
              addQuery("page", page);
            }}
            siblingCount={1}
            totalCount={rides?.meta?.totalPages || 0}
            pageSize={pageSize}
          />
        )}
      </div>
      <CModal
        alignment="center"
        visible={modalState.visible}
        onClose={() => setModalState({ ...modalState, visible: false })}
      >
        <PayRideModalContent
          target={modalState.target}
          initialValue={
            modalState.target === "balance"
              ? balanceAmountTotal // Total of final prices
              : discountAmountTotal // Total of coupon discounts
          }
          onClose={() => {
            setModalState({ ...modalState, visible: false });
            dispatch({ type: "", id: "" });
          }}
          driverId={driver.id}
          rides={selectedRides.ids}
        />
      </CModal>
    </div>
  );
}
